import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    TextField,
    Typography,
    styled,
    useTheme,
} from '@mui/material';

const SearchTextField = styled(TextField)(({ theme }) => ({
    borderColor: theme.palette.grey[700],
    borderRadius: '6px',
    '& input': {
        color: theme.palette.grey[700],
    },
    width: '100%',
}));

const MainSection = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    paddingRight: '20px',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
});

const FilterSection = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
    alignItems: 'center',
    flexWrap: 'wrap',
    flex: 1,
});

const Count = styled(Typography)({
    fontSize: '16px',
    fontWeight: 500,
    color: '#434546',
});

const TableFilter = ({ search, handleChange, filterData, apply, reset, setLoad, count, setPage }) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);
    const [status, setStatus] = useState('');
    const [dedicatedUserId, setDedicatedUserId] = useState('');
    const [isDedicated, setIsDedicated] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const open = Boolean(anchorEl);
    const hasActiveFilters =
        (filterData?.status !== '' && filterData?.status !== null && filterData?.status !== undefined) ||
        Boolean(filterData?.dedicatedUserId?.trim()) ||
        (filterData?.isDedicated !== '' && filterData?.isDedicated !== null && filterData?.isDedicated !== undefined);
    const hasActiveSearchOrFilters = Boolean(appliedSearch) || hasActiveFilters;

    useEffect(() => {
        if (!open) return;
        setStatus(filterData?.status ?? '');
        setDedicatedUserId(filterData?.dedicatedUserId ?? '');
        setIsDedicated(filterData?.isDedicated ?? '');
    }, [open, filterData?.status, filterData?.dedicatedUserId, filterData?.isDedicated]);

    const handleSearch = () => {
        setAppliedSearch(search.trim());
        setPage(0);
        setLoad(true);
    };

    const handleClearAll = () => {
        handleChange('');
        setAppliedSearch('');
        setStatus('');
        setDedicatedUserId('');
        setIsDedicated('');
        reset();
        setAnchorEl(null);
        setPage(0);
        setLoad(true);
    };

    const applyFilters = () => {
        apply({ status, dedicatedUserId: dedicatedUserId.trim(), isDedicated });
        setAnchorEl(null);
    };

    const resetFilters = () => {
        setStatus('');
        setDedicatedUserId('');
        setIsDedicated('');
        reset();
        setAnchorEl(null);
    };

    return (
        <MainSection>
            <FilterSection>
                <SearchTextField
                    type="text"
                    id="searchEmailId"
                    onChange={(e) => handleChange(e.target.value)}
                    value={search}
                    size="small"
                    placeholder="Search"
                    variant="outlined"
                    sx={{ width: { xs: '100%', md: 220 }, minWidth: 160 }}
                />
                <Button
                    variant="contained"
                    size="medium"
                    sx={{
                        color: '#fff',
                        backgroundColor: theme.palette.primary.main,
                        boxShadow: 'none',
                        '&:hover': {
                            color: '#fff',
                            backgroundColor: theme.palette.primary.main,
                        },
                    }}
                    onClick={handleSearch}
                >
                    Search
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    id="gateway-filter-button"
                    aria-controls={open ? 'gateway-filter-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    sx={{
                        position: 'relative',
                        '&::after': hasActiveFilters ? {
                            content: '""',
                            position: 'absolute',
                            width: 8,
                            height: 8,
                            top: 4,
                            right: 4,
                            borderRadius: '50%',
                            backgroundColor: 'primary.main',
                        } : undefined,
                    }}
                >
                    Filters
                </Button>
                {hasActiveSearchOrFilters && (
                    <Button variant="outlined" size="small" onClick={handleClearAll}>
                        Clear All
                    </Button>
                )}
                <Menu
                    id="gateway-filter-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    MenuListProps={{ 'aria-labelledby': 'gateway-filter-button' }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', p: 2.5, gap: 2, width: 320 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="gateway-status-filter-label">Status</InputLabel>
                            <Select
                                labelId="gateway-status-filter-label"
                                id="gateway-status-filter"
                                value={status}
                                label="Status"
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value={0}>Pending</MenuItem>
                                <MenuItem value={1}>Active</MenuItem>
                                <MenuItem value={2}>Only Report</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="gateway-dedicated-filter-label">Dedicated</InputLabel>
                            <Select
                                labelId="gateway-dedicated-filter-label"
                                id="gateway-dedicated-filter"
                                value={isDedicated}
                                label="Dedicated"
                                onChange={(e) => setIsDedicated(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value={1}>Yes</MenuItem>
                                <MenuItem value={0}>No</MenuItem>
                            </Select>
                        </FormControl>
                        <SearchTextField
                            type="text"
                            id="dedicatedUserId"
                            onChange={(e) => setDedicatedUserId(e.target.value)}
                            value={dedicatedUserId}
                            size="small"
                            placeholder="Dedicated User ID"
                            variant="outlined"
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button variant="contained" sx={{ boxShadow: 'none' }} onClick={applyFilters}>
                                Apply
                            </Button>
                            <Button variant="outlined" color="warning" sx={{ boxShadow: 'none' }} onClick={resetFilters}>
                                Reset Filters
                            </Button>
                            <Button variant="outlined" sx={{ boxShadow: 'none' }} onClick={() => setAnchorEl(null)}>
                                Close
                            </Button>
                        </Box>
                    </Box>
                </Menu>
            </FilterSection>
            <Count>Total Gateway : {count}</Count>
        </MainSection>
    );
};

export default TableFilter;
